import React, { useState } from 'react';
import { Button, Space, Tag, Input, InputNumber, Modal, Alert, Descriptions, Empty, Spin, Upload, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
    GET_DSP_INVOICE_BY_BILL_ID,
    GET_HUB_DSP_INVOICE_BY_BILL_ID,
    GET_MY_DSP_INVOICE_BY_BILL_ID,
    VALIDATE_MY_DSP_INVOICE_FINANCE_INFO,
} from '../../graphql/queries/settlement/dspInvoiceQueries';
import {
    CONFIRM_MY_SELF_MADE_DSP_INVOICE,
    GENERATE_MY_DSP_INVOICE,
    REGENERATE_MY_DSP_INVOICE,
    SUBMIT_MY_DSP_INVOICE,
    UPLOAD_MY_SELF_MADE_DSP_INVOICE,
    WITHDRAW_MY_DSP_INVOICE_SUBMIT,
} from '../../graphql/mutations/dspInvoice';
import {
    DspInvoiceStatus,
    DspInvoiceType,
    DspFinanceMissingFieldType,
    DspSelfMadeInvoiceUploadType,
} from '../../graphql/types/settlement/dspInvoice';
import { formatCentsAmount, formatYyyymmdd } from '../../utils/invoiceAmount';
import { formatSettlementAmountFromMajor } from '../../utils/settlementAmountFormatter';
import { showError } from '../../utils/exceptionUtils';
import DocxPreviewFrame from './DocxPreviewFrame';

interface DspInvoiceCardProps {
    billId: string;
    /** 账单状态字符串，如 EXTERNAL_CONFIRMED / INVOICE_CONFIRMED / PAID */
    billStatus?: string | null;
    /** 使用方：dsp 端可生成/重新生成/提交；central 与 hub 端纯只读 */
    source?: 'dsp' | 'central' | 'hub';
    /** source=hub 时必传，用于 Hub 归属校验 */
    hubId?: number;
    /** 账单金额（账单详情接口的 billAmount，主币种单位），传入时在弹框中与 Invoice 金额并列展示 */
    billAmount?: string | number | null;
    /** 账单币种，配合 billAmount 格式化 */
    billCurrency?: string | null;
    /** 财务驳回原因（仅 source=dsp 使用），Invoice 未提交时在弹框顶部展示 */
    rejectReason?: string | null;
}

/** PDF 渲染状态标签；「已提交」是独立维度，另行展示 */
const STATUS_TAG: Record<number, { color: string; key: string }> = {
    [DspInvoiceStatus.INITIATED]: { color: 'processing', key: 'generating' },
    [DspInvoiceStatus.PDF_READY]: { color: 'blue', key: 'pdfReady' },
    [DspInvoiceStatus.FAILED]: { color: 'red', key: 'failed' },
    [DspInvoiceStatus.CANCELLED]: { color: 'default', key: 'cancelled' },
};

/** 读成 base64（去掉 dataURL 前缀），与 QcPenaltyUpload 等既有上传页保持一致 */
const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const dataUrl = reader.result;
            if (typeof dataUrl !== 'string') {
                reject(new Error('Failed to read file'));
                return;
            }
            const comma = dataUrl.indexOf(',');
            if (comma === -1) {
                reject(new Error('Failed to read file'));
                return;
            }
            resolve(dataUrl.slice(comma + 1));
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });

/**
 * 账单详情页「Invoice」入口：页头一个按钮，点击弹出 Invoice 详情弹窗。
 *
 * 可操作窗口：账单处于 EXTERNAL_CONFIRMED(外部已确认) 时才允许生成/重新生成/提交；
 * 一旦财务确认（账单进入 INVOICE_CONFIRMED 及之后），Invoice 不可再修改，仅保留预览。
 */
const DspInvoiceCard: React.FC<DspInvoiceCardProps> = ({
    billId,
    billStatus,
    source = 'dsp',
    hubId,
    billAmount,
    billCurrency,
    rejectReason
}) => {
    const { t } = useTranslation();
    const client = useApolloClient();

    const [open, setOpen] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [isRegenerate, setIsRegenerate] = useState(false);
    const [invoiceNo, setInvoiceNo] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const query =
        source === 'central'
            ? GET_DSP_INVOICE_BY_BILL_ID
            : source === 'hub'
              ? GET_HUB_DSP_INVOICE_BY_BILL_ID
              : GET_MY_DSP_INVOICE_BY_BILL_ID;

    const { data, loading, refetch } = useQuery(query, {
        variables: source === 'hub' ? { billId, hubId } : { billId },
        fetchPolicy: 'cache-and-network',
        skip: !billId || (source === 'hub' && hubId == null),
    });
    const invoice: DspInvoiceType | null =
        data?.getMyDspInvoiceByBillId ?? data?.getDspInvoiceByBillId ?? data?.getHubDspInvoiceByBillId ?? null;

    const [generateInvoice] = useMutation(GENERATE_MY_DSP_INVOICE);
    const [regenerateInvoice] = useMutation(REGENERATE_MY_DSP_INVOICE);
    // 提交后服务端清空驳回原因，刷新账单详情使页面顶部的驳回提示条消失
    const [submitInvoice] = useMutation(SUBMIT_MY_DSP_INVOICE, { refetchQueries: ['GetDeliveryDspBillDetail'] });
    const [withdrawInvoiceSubmit] = useMutation(WITHDRAW_MY_DSP_INVOICE_SUBMIT);
    const [confirmSelfMade] = useMutation(CONFIRM_MY_SELF_MADE_DSP_INVOICE);
    const [uploadSelfMade] = useMutation(UPLOAD_MY_SELF_MADE_DSP_INVOICE);

    // 自制 Invoice：上传后先落 S3 并尝试解析，解析结果交由 DSP 在弹窗中确认或改写
    const [uploading, setUploading] = useState(false);
    const [selfMadeOpen, setSelfMadeOpen] = useState(false);
    const [uploadResult, setUploadResult] = useState<DspSelfMadeInvoiceUploadType | null>(null);
    const [selfMadeNo, setSelfMadeNo] = useState('');
    /** 页面以「元」录入，提交时换算为分，避免浮点误差 */
    const [selfMadeAmount, setSelfMadeAmount] = useState<number | null>(null);

    // 仅 DSP 端可操作；账单被财务确认后 Invoice 锁定，故只在外部已确认阶段开放
    const editable = source === 'dsp' && billStatus === 'EXTERNAL_CONFIRMED';
    // 已提交与否取自账单 invoice_ids，与 PDF 状态无关
    const submitted = invoice?.submitted === true;
    // PDF 未就绪时不允许提交：财务确认时必须有 PDF 可审
    const pdfReady = invoice?.status === DspInvoiceStatus.PDF_READY;
    // 自制件可为 docx：PDF 走 iframe 直连预签名 URL，docx 交给 DocxPreviewFrame 客户端渲染。
    // 判据是文件类型而非 pdfPreviewUrl 是否为空 —— 后者的语义是「PDF 尚未渲染完」。
    // fileExtension 缺失（结算服务未升级）时按 pdf 兜底，行为与历史一致
    const previewable = (invoice?.fileExtension ?? 'pdf').toLowerCase() === 'pdf';

    /** 生成前先跑财务档案完整性校验，缺字段时引导去「财务信息」页补全 */
    const openForm = async (regenerate: boolean) => {
        try {
            const { data: checkData } = await client.query({
                query: VALIDATE_MY_DSP_INVOICE_FINANCE_INFO,
                variables: { billId },
                fetchPolicy: 'network-only',
            });
            const missing: DspFinanceMissingFieldType[] = checkData?.validateMyDspInvoiceFinanceInfo ?? [];
            if (missing.length > 0) {
                Modal.warning({
                    title: t('dspInvoice.financeIncomplete.title'),
                    content: (
                        <div>
                            <div className="mb-2">{t('dspInvoice.financeIncomplete.desc')}</div>
                            <ul className="pl-4 list-disc">
                                {missing.map(m => (
                                    <li key={m.fieldName}>{m.label}</li>
                                ))}
                            </ul>
                        </div>
                    ),
                });
                return;
            }
            setIsRegenerate(regenerate);
            setInvoiceNo(regenerate ? '' : invoice?.invoiceNo ?? '');
            setFormOpen(true);
        } catch (error) {
            showError(error);
        }
    };

    const handleConfirmGenerate = async () => {
        if (!invoiceNo.trim()) {
            message.warning(t('dspInvoice.messages.invoiceNoRequired') as string);
            return;
        }
        setSubmitting(true);
        try {
            const variables = {
                billId,
                invoiceNo: invoiceNo.trim(),
                // 按浏览器时区确定开票日与到期日的自然日
                clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            };
            if (isRegenerate) {
                await regenerateInvoice({ variables });
            } else {
                await generateInvoice({ variables });
            }
            message.success(t('dspInvoice.messages.generateSuccess') as string);
            setFormOpen(false);
            await refetch();
        } catch (error) {
            showError(error);
        } finally {
            setSubmitting(false);
        }
    };

    /** 选择文件后立即上传并解析；解析成功则预填，失败则留空由 DSP 手填 */
    const handleUploadSelfMade = async (file: File) => {
        setUploading(true);
        try {
            const fileBase64 = await readFileAsBase64(file);
            const { data: uploadData } = await uploadSelfMade({
                variables: { billId, fileBase64, fileName: file.name },
            });
            const result: DspSelfMadeInvoiceUploadType | undefined = uploadData?.uploadMySelfMadeDspInvoice;
            if (!result) {
                return;
            }
            setUploadResult(result);
            setSelfMadeNo(result.invoiceNo ?? '');
            setSelfMadeAmount(result.totalAmountDue != null ? result.totalAmountDue / 100 : null);
            setSelfMadeOpen(true);
        } catch (error) {
            showError(error);
        } finally {
            setUploading(false);
        }
    };

    const handleConfirmSelfMade = async () => {
        if (!uploadResult?.objectKey) {
            return;
        }
        if (!selfMadeNo.trim()) {
            message.warning(t('dspInvoice.messages.invoiceNoRequired') as string);
            return;
        }
        if (selfMadeAmount == null || selfMadeAmount <= 0) {
            message.warning(t('dspInvoice.messages.amountRequired') as string);
            return;
        }
        setSubmitting(true);
        try {
            await confirmSelfMade({
                variables: {
                    billId,
                    objectKey: uploadResult.objectKey,
                    fileName: uploadResult.fileName,
                    invoiceNo: selfMadeNo.trim(),
                    // 元 → 分，四舍五入到分，规避 0.1+0.2 类浮点误差
                    totalAmountDue: Math.round(selfMadeAmount * 100),
                    parseFailed: !uploadResult.parsed,
                    clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
            });
            message.success(t('dspInvoice.messages.uploadSuccess') as string);
            setSelfMadeOpen(false);
            setUploadResult(null);
            await refetch();
        } catch (error) {
            showError(error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = () => {
        Modal.confirm({
            title: t('dspInvoice.submitConfirm.title'),
            content: t('dspInvoice.submitConfirm.content'),
            onOk: async () => {
                try {
                    await submitInvoice({ variables: { billId } });
                    message.success(t('dspInvoice.messages.submitSuccess') as string);
                    await refetch();
                } catch (error) {
                    showError(error);
                }
            },
        });
    };

    /** 财务确认前撤回提交：Invoice 不作废，撤回后回到「已生成未提交」，可原样重提或替换 */
    const handleWithdraw = () => {
        Modal.confirm({
            title: t('dspInvoice.withdrawConfirm.title'),
            content: t('dspInvoice.withdrawConfirm.content'),
            onOk: async () => {
                try {
                    await withdrawInvoiceSubmit({ variables: { billId } });
                    message.success(t('dspInvoice.messages.withdrawSuccess') as string);
                    await refetch();
                } catch (error) {
                    showError(error);
                }
            },
        });
    };

    const statusTag = invoice?.status != null ? STATUS_TAG[invoice.status] : undefined;
    // Invoice 收进弹窗后，自制标识必须在入口按钮上外显，否则财务要逐单点开才知道是不是自制件
    const selfMadeBadge = invoice?.isSelfMade ? (
        <Tag color="orange" style={{ marginInlineEnd: 0 }}>
            {t('dspInvoice.selfMade.badge')}
        </Tag>
    ) : null;

    /**
     * 上传自制 Invoice：无 Invoice 时为首次开票，已有未提交 Invoice 时为「用自制件替换」，
     * 故与「重新生成」同一展示条件，两种状态各渲染一次。
     */
    const uploadSelfMadeButton = (
        <Upload
            accept=".pdf,.docx"
            showUploadList={false}
            beforeUpload={file => {
                handleUploadSelfMade(file as File);
                // 阻断 antd 默认上传行为，文件由我们自己发到后端接口
                return Upload.LIST_IGNORE;
            }}
        >
            <Button loading={uploading}>{t('dspInvoice.actions.uploadSelfMade')}</Button>
        </Upload>
    );

    /** 原先挂在 Card.extra 的操作区，现移入弹窗正文顶部 */
    const actions = (
        <Space>
            {editable && !invoice && (
                <>
                    <Button type="primary" onClick={() => openForm(false)}>
                        {t('dspInvoice.actions.generate')}
                    </Button>
                    {uploadSelfMadeButton}
                </>
            )}
            {editable && invoice && !submitted && (
                <>
                    <Button onClick={() => openForm(true)}>{t('dspInvoice.actions.regenerate')}</Button>
                    {uploadSelfMadeButton}
                    <Button
                        type="primary"
                        disabled={!pdfReady}
                        title={pdfReady ? undefined : (t('dspInvoice.messages.pdfGenerating') as string)}
                        onClick={handleSubmit}
                    >
                        {t('dspInvoice.actions.submit')}
                    </Button>
                </>
            )}
            {editable && invoice && submitted && (
                <Button danger onClick={handleWithdraw}>
                    {t('dspInvoice.actions.withdraw')}
                </Button>
            )}
            {invoice?.pdfDownloadUrl && (
                <Button href={invoice.pdfDownloadUrl} target="_blank" rel="noreferrer">
                    {t('dspInvoice.actions.download')}
                </Button>
            )}
            {/* PDF 为异步渲染，且只读端需等 DSP 提交，故三端常显刷新 */}
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => refetch()}>
                {t('dspInvoice.actions.refresh')}
            </Button>
        </Space>
    );

    /**
     * docx 预览不可用时的降级呈现：提示 + 下载原件（原始文件名由 docx 分支常显，此处不重复）。
     * 触发场景包括存储桶 CORS 未放行 GET、渲染抛错、畸形或加密 docx。
     * 预览失败不得阻断流程，故这里只降级不报错。
     */
    const renderDocxFallback = (inv: DspInvoiceType) => (
        <Alert
            type="info"
            showIcon
            message={t('dspInvoice.messages.docxNoPreview')}
            action={
                inv.pdfDownloadUrl ? (
                    <Button type="primary" href={inv.pdfDownloadUrl} target="_blank" rel="noreferrer">
                        {t('dspInvoice.actions.download')}
                    </Button>
                ) : undefined
            }
        />
    );

    /*
     * 只读端（总部 / HUB）拿不到 Invoice 时整个入口都不渲染 —— 对它们来说「DSP 未提交」与「尚未开票」
     * 是同一件事，给个点进去只显示「暂无 Invoice」的按钮纯属噪音。
     * 总部侧的未提交过滤在服务端（DspInvoiceQuery.getDspInvoiceByBillId），前端只是不渲染空壳。
     * DSP 端必须保留入口：无票时正是从这里点「生成 Invoice」。
     */
    if (source !== 'dsp' && !invoice) {
        return null;
    }

    return (
        <>
            <Button onClick={() => setOpen(true)}>
                <Space size={4}>
                    {t('dspInvoice.title')}
                    {selfMadeBadge}
                </Space>
            </Button>
            <Modal
                open={open}
                onCancel={() => setOpen(false)}
                footer={null}
                width={960}
                title={
                    <Space size={8}>
                        {t('dspInvoice.title')}
                        {selfMadeBadge}
                    </Space>
                }
            >
                {source === 'dsp' && rejectReason && !submitted && (
                    <Alert
                        type="error"
                        showIcon
                        className="mb-3"
                        message={t('dspInvoice.reject.reasonBanner', { reason: rejectReason })}
                    />
                )}
                <div className="mb-3 text-right">{actions}</div>
                {loading && !invoice ? (
                    <div className="py-8 text-center">
                        <Spin />
                    </div>
                ) : !invoice ? (
                    <Empty
                        description={editable ? t('dspInvoice.empty.canGenerate') : t('dspInvoice.empty.notYet')}
                    />
                ) : (
                    <>
                        {/* amountMismatch 仅总部接口计算，HUB / DSP Owner 拿到的是 null，天然不展示 */}
                        {invoice.amountMismatch && (
                            <Alert
                                type="error"
                                showIcon
                                className="mb-3"
                                message={t('dspInvoice.amountMismatch')}
                            />
                        )}
                        {invoice.isSelfMade && (
                            <Alert
                                // 自制件提示统一用黄色，与详情页一致；红色留给金额不一致提示，避免两条横幅混淆
                                type="warning"
                                showIcon
                                className="mb-3"
                                message={
                                    invoice.isParseFailed
                                        ? t('dspInvoice.selfMade.parseFailed')
                                        : t('dspInvoice.selfMade.hint')
                                }
                            />
                        )}
                        <Descriptions column={2} size="small" bordered className="mb-3">
                            <Descriptions.Item label={t('dspInvoice.fields.invoiceNo')}>
                                {invoice.invoiceNo || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('dspInvoice.fields.source')}>
                                {invoice.isSelfMade ? (
                                    <Tag color="orange">{t('dspInvoice.source.selfMade')}</Tag>
                                ) : (
                                    <Tag color="blue">{t('dspInvoice.source.system')}</Tag>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('dspInvoice.fields.status')}>
                                {statusTag ? (
                                    <Tag color={statusTag.color}>{t(`dspInvoice.status.${statusTag.key}`)}</Tag>
                                ) : (
                                    '-'
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('dspInvoice.fields.invoiceAmount')}>
                                <span className={invoice.amountMismatch ? 'font-bold text-red-600' : undefined}>
                                    {formatCentsAmount(invoice.invoiceAmount, invoice.currency)}
                                </span>
                            </Descriptions.Item>
                            {billAmount != null && (
                                <Descriptions.Item label={t('dspInvoice.fields.billAmount')}>
                                    {formatSettlementAmountFromMajor(billAmount, billCurrency)}
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label={t('dspInvoice.fields.invoiceDate')}>
                                {formatYyyymmdd(invoice.invoiceDate)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('dspInvoice.fields.submitted')}>
                                {submitted ? (
                                    <Tag color="green">{t('dspInvoice.submitted.yes')}</Tag>
                                ) : (
                                    <Tag>{t('dspInvoice.submitted.no')}</Tag>
                                )}
                            </Descriptions.Item>
                        </Descriptions>

                        {!previewable ? (
                            // docx：取原件字节在客户端渲染，渲染不出来就退回下载核对。
                            // 原始文件名在两种结果下都要看得到，故提到分支外常显
                            <>
                                {invoice.fileName && (
                                    <div className="mb-2 text-gray-500">
                                        {`${t('dspInvoice.fields.fileName')}: ${invoice.fileName}`}
                                    </div>
                                )}
                                {invoice.pdfPreviewUrl ? (
                                    <DocxPreviewFrame
                                        url={invoice.pdfPreviewUrl}
                                        renderError={renderDocxFallback(invoice)}
                                    />
                                ) : (
                                    renderDocxFallback(invoice)
                                )}
                            </>
                        ) : invoice.pdfPreviewUrl ? (
                            <iframe
                                title={t('dspInvoice.title') as string}
                                src={invoice.pdfPreviewUrl}
                                className="w-full border border-gray-200"
                                style={{ height: 720 }}
                            />
                        ) : (
                            <Alert type="info" showIcon message={t('dspInvoice.messages.pdfGenerating')} />
                        )}
                    </>
                )}

                <Modal
                    open={formOpen}
                    title={
                        isRegenerate ? t('dspInvoice.actions.regenerate') : t('dspInvoice.actions.generate')
                    }
                    onCancel={() => setFormOpen(false)}
                    onOk={handleConfirmGenerate}
                    confirmLoading={submitting}
                    destroyOnClose
                >
                    {isRegenerate && (
                        <Alert
                            type="warning"
                            showIcon
                            className="mb-3"
                            message={t('dspInvoice.regenerateHint')}
                        />
                    )}
                    <div className="mb-2">{t('dspInvoice.fields.invoiceNo')}</div>
                    <Input
                        value={invoiceNo}
                        onChange={e => setInvoiceNo(e.target.value)}
                        maxLength={64}
                        showCount
                        placeholder={t('dspInvoice.placeholders.invoiceNo') as string}
                    />
                </Modal>
                <Modal
                    open={selfMadeOpen}
                    title={t('dspInvoice.selfMade.confirmTitle')}
                    onCancel={() => setSelfMadeOpen(false)}
                    onOk={handleConfirmSelfMade}
                    confirmLoading={submitting}
                    destroyOnClose
                >
                    {/* 已有 Invoice 时本次上传是替换而非新增，后果必须在确认前讲清 */}
                    {invoice && (
                        <Alert
                            type="warning"
                            showIcon
                            className="mb-3"
                            message={t('dspInvoice.selfMade.replaceHint')}
                        />
                    )}
                    <Alert
                        type={uploadResult?.parsed ? 'success' : 'warning'}
                        showIcon
                        className="mb-3"
                        message={
                            uploadResult?.parsed
                                ? t('dspInvoice.selfMade.parsedHint')
                                : uploadResult?.failReason || t('dspInvoice.selfMade.manualHint')
                        }
                    />
                    <div className="mb-1">{t('dspInvoice.fields.fileName')}</div>
                    <div className="mb-3 text-gray-500">{uploadResult?.fileName || '-'}</div>

                    <div className="mb-1">{t('dspInvoice.fields.invoiceNo')}</div>
                    <Input
                        className="mb-3"
                        value={selfMadeNo}
                        onChange={e => setSelfMadeNo(e.target.value)}
                        maxLength={64}
                        showCount
                        placeholder={t('dspInvoice.placeholders.invoiceNo') as string}
                    />

                    <div className="mb-1">{t('dspInvoice.fields.totalAmountDue')}</div>
                    <InputNumber
                        className="w-full"
                        value={selfMadeAmount}
                        onChange={v => setSelfMadeAmount(v as number | null)}
                        min={0}
                        precision={2}
                        placeholder={t('dspInvoice.placeholders.totalAmountDue') as string}
                    />
                    <div className="mt-1 text-gray-400 text-xs">{t('dspInvoice.selfMade.amountMustMatch')}</div>
                </Modal>
            </Modal>
        </>
    );
};

export default DspInvoiceCard;
